import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideCircleCheck,
  lucideTriangleAlert,
  lucideUsers,
  lucideX,
} from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { LoggerService } from '../../services/logger.service';

import { Router, ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CompanyService } from '../../services/company.service';
import { StripeService } from '../../services/stripe.service';
import { AuthService } from '../../services/auth.service';
import {
  CompanyNotificationService,
  CompanyNotification,
} from '../../services/company-notification.service';
import type { Company } from '../../services/company.service';

interface StripeComponent extends Node {
  setOnEvent?: (callback: (event: any) => void) => void;
  setOnLoaderStart?: (callback: (event: { elementTagName: string }) => void) => void;
  destroy?: () => void;
}

@Component({
  selector: 'app-notifications',
  standalone: true,
  imports: [NgIcon, HlmAlertImports, HlmButtonImports],
  providers: [
    provideIcons({
      lucideArrowRight,
      lucideCircleCheck,
      lucideTriangleAlert,
      lucideUsers,
      lucideX,
    }),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './notifications.component.html',
  // Without this the host stays inline and the mt-4 of a banner would collapse.
  host: { class: 'block' },
})
export class NotificationsComponent implements OnInit, OnDestroy {
  showDoStripeSetup = false;
  showOnboardingSuccess = false;
  private isOnboardingSuccess = false;
  private destroy$ = new Subject<void>();
  private stripeNotificationBanner: StripeComponent | null = null;
  /** The banner is created once per login, and only for a company with Stripe. */
  private stripeBannerStarted = false;
  company: Company | null = null;

  // Group-join notifications shown as dismissible banners
  groupJoinNotifications: CompanyNotification[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private companyService: CompanyService,
    private stripeService: StripeService,
    private authService: AuthService,
    private logger: LoggerService,
    private notifService: CompanyNotificationService,
  ) {}

  ngOnInit() {
    this.logger.log('🔔 [NotificationsComponent] Component initializing');

    // Listen to route changes to update notifications
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      this.isOnboardingSuccess = params['onboarding_success'] === 'true';
      this.logger.log('🔄 [NotificationsComponent] Route params changed:', {
        isOnboardingSuccess: this.isOnboardingSuccess,
      });

      // Update alerts when route changes
      if (this.company) {
        this.updateAlerts(this.company);
      }
    });

    // Listen to company changes
    this.listenToCompanyChanges();

    // Listen for unread group-join notifications to surface as banners
    this.notifService.notifications$.pipe(takeUntil(this.destroy$)).subscribe((notifs) => {
      this.groupJoinNotifications = notifs.filter(
        (n) => n.type === 'group_member_joined' && !n.is_read,
      );
    });

    // A new login may be another company: decide about the Stripe banner again once it loads.
    this.authService.loginSuccess$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.stripeBannerStarted = false;
    });
  }

  private listenToCompanyChanges() {
    this.companyService.company$.pipe(takeUntil(this.destroy$)).subscribe((company) => {
      if (company) {
        this.company = company;
        this.updateAlerts(company);
      }
    });
  }

  private updateAlerts(company: Company) {
    // Network members have no Stripe account: none of the Stripe alerts apply to them.
    const hasStripe = !company.is_member;
    // Stripe's banner needs an existing account (a new shop creates it in the setup).
    const hasStripeAccount = hasStripe && !!company.stripe_account_id;
    const isCompanyOnboarded = company.is_stripe_onboarding_success || false;
    const params = this.route.snapshot.queryParams;
    const isSetupStarted = params['setup-started'] === 'true';

    this.showOnboardingSuccess = hasStripe && this.isOnboardingSuccess && isCompanyOnboarded;
    // Hide Stripe setup notification when user is already in setup mode
    this.showDoStripeSetup =
      hasStripe && company.is_stripe_onboarding_success === false && !isSetupStarted;

    if (hasStripeAccount && !this.stripeBannerStarted && this.authService.isLoggedIn()) {
      this.stripeBannerStarted = true;
      this.initializeStripeNotificationBanner();
    }
  }

  dismissOnboardingSuccess() {
    this.showOnboardingSuccess = false;
  }

  dismissGroupJoinNotif(n: CompanyNotification): void {
    this.notifService.markRead(n.id).subscribe();
  }

  goToGroup(n: CompanyNotification): void {
    const groupId = n.data?.['group_public_id'] as string | undefined;
    this.notifService.markRead(n.id).subscribe();
    this.router.navigate(['/settings'], { queryParams: { tab: 'verbund-members' } });
  }

  navigateToStripeSetup() {
    this.router.navigate(['/settings'], {
      queryParams: { tab: 'payments', 'setup-started': 'true' },
    });
  }

  private async initializeStripeNotificationBanner() {
    this.logger.log('🔔 [NotificationsComponent] Initializing Stripe notification banner');

    try {
      this.logger.log('🔄 [NotificationsComponent] Calling StripeService.createStripeComponent()');
      this.stripeNotificationBanner =
        await this.stripeService.createStripeComponent('notification-banner');
      this.logger.log('🔔 [NotificationsComponent] Notification banner component created');

      // Mount notification banner
      const bannerContainer = document.getElementById('stripe-notification-banner-mount');
      if (bannerContainer && !bannerContainer.hasChildNodes() && this.stripeNotificationBanner) {
        bannerContainer.appendChild(this.stripeNotificationBanner);
        this.logger.log('📌 [NotificationsComponent] Notification banner mounted to DOM');

        // Set up event handler if available
        if (this.stripeNotificationBanner.setOnEvent) {
          this.stripeNotificationBanner.setOnEvent((event: any) => {
            // this.logger.log('Stripe notification banner event:', event);
          });
          this.logger.log(
            '🎯 [NotificationsComponent] Event handler set up for notification banner',
          );
        }
      } else {
        this.logger.warn('⚠️ [NotificationsComponent] Could not mount notification banner:', {
          hasContainer: !!bannerContainer,
          containerHasChildren: bannerContainer ? bannerContainer.hasChildNodes() : false,
          hasBanner: !!this.stripeNotificationBanner,
        });
      }
    } catch (err: any) {
      this.logger.error(
        '❌ [NotificationsComponent] Failed to initialize Stripe notification banner:',
        err,
      );
    }
  }

  ngOnDestroy() {
    if (
      this.stripeNotificationBanner &&
      typeof this.stripeNotificationBanner.destroy === 'function'
    ) {
      this.stripeNotificationBanner.destroy();
    }

    this.destroy$.next();
    this.destroy$.complete();
  }
}
